import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function NewsletterForm() {
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState(""); // honeypot
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!isEmail(email)) {
            setError("Please enter a valid email.");
            return;
        }
        setStatus("loading");
        try {
            const res = await fetch(`${API}/newsletter`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, website }),
            });
            if (!res.ok) throw new Error("Subscription failed");
            setStatus("success");
            setEmail("");
        } catch {
            setStatus("error");
            setError(
                "We couldn't process that just now. Please try again shortly.",
            );
        }
    };

    return (
        <div data-testid="newsletter-form">
            <p className="eyebrow mb-3">The Journal · Private Dispatch</p>
            <p className="text-[#9A958A] text-sm leading-[1.85] mb-5 max-w-sm">
                Occasional notes on architecture, technology, and craft.
            </p>

            <AnimatePresence mode="wait">
                {status === "success" ? (
                    <motion.div
                        key="ok"
                        suppressHydrationWarning
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 0.5,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="flex items-center gap-3 text-[#C9A86A] text-sm"
                        data-testid="newsletter-success"
                    >
                        <Check size={16} strokeWidth={1.4} />
                        <span>
                            Thank you. You&rsquo;re on the dispatch list.
                        </span>
                    </motion.div>
                ) : (
                    <motion.form
                        key="form"
                        initial={false}
                        onSubmit={onSubmit}
                        className="flex flex-col sm:flex-row gap-3 max-w-sm"
                        noValidate
                    >
                        {/* Honeypot — visually hidden, off-keyboard */}
                        <input
                            type="text"
                            name="website"
                            tabIndex="-1"
                            autoComplete="off"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            aria-hidden="true"
                            style={{
                                position: "absolute",
                                left: "-9999px",
                                width: "1px",
                                height: "1px",
                                opacity: 0,
                            }}
                            data-testid="newsletter-honeypot"
                        />

                        <label htmlFor="newsletter-email" className="sr-only">
                            Email address
                        </label>
                        <input
                            id="newsletter-email"
                            type="email"
                            inputMode="email"
                            placeholder="your@email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (status === "error") setStatus("idle");
                            }}
                            required
                            aria-describedby={error ? "newsletter-error" : undefined}
                            className="flex-1 bg-transparent border-b border-[rgba(201,168,106,0.3)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-sm py-2.5 px-1 transition-colors duration-300 placeholder:text-[#5e5a52]"
                            data-testid="newsletter-email"
                        />
                        <button
                            type="submit"
                            disabled={status === "loading"}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#C9A86A] text-[10.5px] tracking-[0.26em] uppercase text-[#C9A86A] hover:bg-[#C9A86A] hover:text-[#0B0B0C] transition-colors duration-400 disabled:opacity-60 disabled:cursor-wait"
                            data-testid="newsletter-submit"
                        >
                            {status === "loading" ? "Sending…" : "Subscribe"}
                            {status !== "loading" && (
                                <ArrowUpRight size={13} strokeWidth={1.2} />
                            )}
                        </button>
                    </motion.form>
                )}
            </AnimatePresence>

            {error && status !== "success" && (
                <p
                    id="newsletter-error"
                    role="alert"
                    className="mt-3 text-[12px] text-[#C97A6A]"
                    data-testid="newsletter-error"
                >
                    {error}
                </p>
            )}
        </div>
    );
}
